import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import PlacemarkInfoPanel from './PlacemarkInfoPanel.jsx';
import MapLegend from './MapLegend.jsx';
import MapRangeSlider from './MapRangeSlider.jsx';
import Utils from '../util/Utils.jsx';
import { applyMapLabelsAndBoundaries } from '../util/MapUtil.jsx';
import useGoogleMap from './maps/useGoogleMap.js';
import {
	applyPlacemarkFilterStyles,
	applyPlacemarkSelectionStyles,
	applyDefaultPlacemarkStyles,
	applyUngroupedPlacemarkStyles,
	startStrokePulse,
	stopStrokePulse,
	DEFAULT_PLACEMARK_ITEM_STYLES
} from './maps/mapAdapter.js';
import {
	bindActivitiesToPlacemarks,
	placemarkInfoWindowHtml,
	resolveKmlIconHref,
	summarizePinCodeMatch
} from '../util/kmlMap.js';
import MapMatchReport from './MapMatchReport.jsx';
import useProximity from '../hooks/useProximity.js';
import { useStateValue } from '../context/state.jsx';

if (typeof window !== 'undefined') {
	window.swaResolveKmlIconHref = resolveKmlIconHref;
	window.swaPlacemarkInfoWindowHtml = placemarkInfoWindowHtml;
}

const MapView = ({
	alert,
	filteredActivities,
	filteredActivitiesMap,
	projectMapGroupBy,
	datasetInfo,
	proximityDistance,
	mapPinMyLocation,
	mapPinPickMode,
	setMapPinMyLocation,
	setMapPinPickMode
}) => {
	const [{ projectActivities }] = useStateValue();
	const mapContainerRef = useRef(null);
	const mapViewRootRef = useRef(null);
	const pinPointMarkerRef = useRef(null);
	const pulseIntervalRef = useRef(null);
	const placemarkItemsRef = useRef({});
	const projectActivitiesRef = useRef(projectActivities);
	const selectedPlacemarkTitleRef = useRef(null);
	const closeSelectedPlacemarkInfoPanelRef = useRef(() => {});
	const kmlParsingCallbackRef = useRef(() => {});
	const proximityAlertKeyRef = useRef(null);
	const mapPinPickModeRef = useRef(false);
	const setMapPinMyLocationRef = useRef(setMapPinMyLocation);
	const setMapPinPickModeRef = useRef(setMapPinPickMode);
	const groundOverlayRef = useRef(null);

	const { mapRef, ready: mapReady } = useGoogleMap(mapContainerRef);

	const [selectedPlacemarkTitle, setSelectedPlacemarkTitle] = useState(null);
	const [placemarkItems, setPlacemarkItems] = useState({});
	const [visibleColorsHashmap, setVisibleColorsHashmap] = useState({});
	const [overlayVisible, setOverlayVisible] = useState(true);

	useEffect(() => {
		setOverlayVisible(true);
	}, [datasetInfo.Id]);

	const proximityActive = mapPinMyLocation != null && proximityDistance != null;
	const { visibleByTitle, hasAnyInProximity } = useProximity(
		placemarkItems,
		proximityActive ? mapPinMyLocation : null,
		proximityActive ? proximityDistance : null
	);

	projectActivitiesRef.current = projectActivities;
	placemarkItemsRef.current = placemarkItems;
	selectedPlacemarkTitleRef.current = selectedPlacemarkTitle;
	mapPinPickModeRef.current = Boolean(mapPinPickMode);
	setMapPinMyLocationRef.current = setMapPinMyLocation;
	setMapPinPickModeRef.current = setMapPinPickMode;

	const closeSelectedPlacemarkInfoPanel = useCallback(() => {
		stopStrokePulse(pulseIntervalRef);
		const items = placemarkItemsRef.current;
		const selectedPlacemarkItem = items[selectedPlacemarkTitleRef.current];
		if (selectedPlacemarkItem) {
			applyDefaultPlacemarkStyles(selectedPlacemarkItem);
			selectedPlacemarkItem.infoWindow.close();
		}
		setSelectedPlacemarkTitle(null);
	}, []);

	closeSelectedPlacemarkInfoPanelRef.current = closeSelectedPlacemarkInfoPanel;

	const onPlacemarkClick = useCallback((clickedPlacemarkItem) => {
		const items = placemarkItemsRef.current;
		setSelectedPlacemarkTitle(clickedPlacemarkItem.title);
		applyPlacemarkSelectionStyles(items, clickedPlacemarkItem.title);
		startStrokePulse(clickedPlacemarkItem, pulseIntervalRef);
	}, []);

	const kmlParsingCallback = useCallback((doc) => {
		const { areaProperty } = datasetInfo;
		const nextPlacemarkItems = {};

		doc[0].placemarks.forEach(placemark => {
			const item = placemark.polygon || placemark.polyline || placemark.marker;
			if (item) {
				if (placemark.polygon) {
					item.type = 'polygon';
					item.coordinates = placemark.Polygon[0].outerBoundaryIs[0].coordinates;
				} else if (placemark.polyline) {
					item.type = 'polyline';
					item.coordinates = placemark.LineString[0].coordinates;
				} else if (placemark.marker) {
					item.type = 'marker';
					item.coordinates = placemark.Point.coordinates;
				}
				item.activities = [];

				nextPlacemarkItems[item.title] = item;

				item.setOptions({
					...DEFAULT_PLACEMARK_ITEM_STYLES[item.type]
				});
				google.maps.event.addListener(
					item,
					'click',
					() => {
						onPlacemarkClick(item);
					}
				);
			} else {
				console.log('no polygon/polyline/marker, or no title.  placemark: ', placemark);
			}
		});

		// Join from the full project activity list — not the current date filter —
		// so the map date slider can hide/show without permanently orphaning shapes.
		bindActivitiesToPlacemarks(
			nextPlacemarkItems,
			projectActivitiesRef.current,
			areaProperty
		);

		setPlacemarkItems(nextPlacemarkItems);
	}, [datasetInfo, onPlacemarkClick]);

	kmlParsingCallbackRef.current = kmlParsingCallback;

	// Mount: configure map type, click-to-deselect, overlay + KML parse.
	useEffect(() => {
		if (!mapReady || !mapRef.current) {
			return undefined;
		}

		const map = mapRef.current;
		applyMapLabelsAndBoundaries(map);
		// Maps created before layout settles stay blank until resize.
		google.maps.event.trigger(map, 'resize');

		const handleMapClick = (event) => {
			if (mapPinPickModeRef.current && event && event.latLng) {
				const lat = event.latLng.lat();
				const lng = event.latLng.lng();
				if (typeof setMapPinMyLocationRef.current === 'function') {
					setMapPinMyLocationRef.current(lat, lng);
				}
				if (typeof setMapPinPickModeRef.current === 'function') {
					setMapPinPickModeRef.current(false);
				}
				return;
			}
			closeSelectedPlacemarkInfoPanelRef.current();
		};

		const mapClickListener = google.maps.event.addListener(
			map,
			'click',
			handleMapClick
		);

		const origin = Utils.getApiOrigin();
		const parserOptions = {
			map,
			singleInfoWindow: true,
			suppressInfoWindows: false,
			suppressDirections: true,
			afterParse: (doc) => {
				kmlParsingCallbackRef.current(doc);
				google.maps.event.trigger(map, 'resize');
			}
		};

		let overlayClickListener = null;
		groundOverlayRef.current = null;
		if (datasetInfo.Overlay) {
			parserOptions.createOverlay = () => {
				const overlayOrigin = Utils.getApiOrigin();
				const url = `${overlayOrigin}/datasets/${datasetInfo.Id}/${datasetInfo.Overlay}`;
				const imageBounds = new google.maps.LatLngBounds(
					new google.maps.LatLng(parseFloat(datasetInfo.South), parseFloat(datasetInfo.West)),
					new google.maps.LatLng(parseFloat(datasetInfo.North), parseFloat(datasetInfo.East))
				);
				const mapOverlay = new google.maps.GroundOverlay(url, imageBounds);
				mapOverlay.setOpacity(0.7);
				mapOverlay.setMap(map);
				mapOverlay.active = true;
				groundOverlayRef.current = mapOverlay;

				overlayClickListener = google.maps.event.addListener(
					mapOverlay,
					'click',
					handleMapClick
				);
				return mapOverlay;
			};
		}

		window.geoXML3.parser(parserOptions).parse(
			`${origin}/datasets/${datasetInfo.Id}/${datasetInfo.Kml}`
		);

		return () => {
			google.maps.event.removeListener(mapClickListener);
			if (overlayClickListener) {
				google.maps.event.removeListener(overlayClickListener);
			}
			groundOverlayRef.current = null;
		};
		// Once per map ready + dataset id — preserve single KML parse lifecycle (class componentDidMount).
	}, [mapReady, mapRef, datasetInfo.Id, datasetInfo.Overlay, datasetInfo.Kml, datasetInfo.South, datasetInfo.West, datasetInfo.North, datasetInfo.East]);

	// Keep ground overlay visibility in sync with the toggle.
	useEffect(() => {
		const overlay = groundOverlayRef.current;
		const map = mapRef.current;
		if (!overlay || !map || !datasetInfo.Overlay) {
			return;
		}
		overlay.setMap(overlayVisible ? map : null);
	}, [overlayVisible, mapReady, mapRef, datasetInfo.Overlay, placemarkItems]);

	// Filter-driven placemark colors / visibility — effect boundary, not render.
	// Re-bind from full projectActivities so date-slider filter changes remain reversible.
	useEffect(() => {
		const activityCodeTypes = datasetInfo.activityCodeTypes || {};
		const { areaProperty } = datasetInfo;
		if (!Object.keys(placemarkItems).length) {
			return;
		}

		bindActivitiesToPlacemarks(placemarkItems, projectActivities, areaProperty);

		const activityCodeType = projectMapGroupBy ? activityCodeTypes[projectMapGroupBy] : null;
		if (!activityCodeType) {
			applyUngroupedPlacemarkStyles(placemarkItems);
			setVisibleColorsHashmap({});
			return;
		}

		const hashmap = applyPlacemarkFilterStyles(placemarkItems, {
			projectMapGroupBy,
			filteredActivitiesMap: filteredActivitiesMap || {},
			activityCodeType
		});
		setVisibleColorsHashmap(hashmap);
		// Re-run when pin clears so filter visibility is restored after proximity hide.
	}, [
		placemarkItems,
		projectActivities,
		filteredActivitiesMap,
		projectMapGroupBy,
		datasetInfo,
		mapPinMyLocation,
		proximityDistance
	]);

	// Proximity pin — Maps marker only; distance math lives in useProximity (WP-3).
	useEffect(() => {
		const map = mapRef.current;

		if (!proximityActive) {
			if (pinPointMarkerRef.current) {
				pinPointMarkerRef.current.setMap(null);
				pinPointMarkerRef.current = null;
			}
			proximityAlertKeyRef.current = null;
			return;
		}

		if (!map) {
			return;
		}

		const { lat, lng } = mapPinMyLocation;
		if (pinPointMarkerRef.current) {
			pinPointMarkerRef.current.setMap(null);
		}

		pinPointMarkerRef.current = new google.maps.Marker({
			position: { lat, lng },
			title: 'My location'
		});
		pinPointMarkerRef.current.setMap(map);

		const items = placemarkItemsRef.current;
		Object.keys(items).forEach(key => {
			const placemark = items[key];
			placemark.setVisible(Boolean(visibleByTitle[placemark.title]));
		});

		const alertKey = `${lat},${lng},${proximityDistance}`;
		if (!hasAnyInProximity && typeof alert === 'function' && proximityAlertKeyRef.current !== alertKey) {
			proximityAlertKeyRef.current = alertKey;
			alert('There are no areas within ' + proximityDistance + ' feet of you.');
		}
	}, [
		proximityActive,
		mapPinMyLocation,
		proximityDistance,
		visibleByTitle,
		hasAnyInProximity,
		placemarkItems,
		mapRef,
		alert
	]);

	useEffect(() => {
		return () => {
			stopStrokePulse(pulseIntervalRef);
			if (pinPointMarkerRef.current) {
				pinPointMarkerRef.current.setMap(null);
				pinPointMarkerRef.current = null;
			}
		};
	}, []);

	const toggleOverlay = () => {
		setOverlayVisible(prev => !prev);
	};

	const stop = (event) => {
		event.stopPropagation();
	};

	const activityCodeTypes = datasetInfo.activityCodeTypes || {};
	const activityCodeType = projectMapGroupBy ? activityCodeTypes[projectMapGroupBy] : null;
	const pinMatchReport = useMemo(() => {
		const areaType = datasetInfo.areaProperty
			? (activityCodeTypes[datasetInfo.areaProperty] || activityCodeTypes[String(datasetInfo.areaProperty)])
			: null;
		const codeValues = areaType && areaType.values
			? Object.keys(areaType.values).map(codeId => areaType.values[codeId])
			: [];
		return {
			areaTypeName: areaType ? areaType.name : '',
			areaTypeSet: Boolean(areaType),
			...summarizePinCodeMatch(Object.keys(placemarkItems), codeValues)
		};
	}, [activityCodeTypes, datasetInfo.areaProperty, placemarkItems]);
	const mapLegendValues = activityCodeType
		? activityCodeType.valuesSequence.map(activityCode => activityCodeType.values[activityCode])
		: [];
	const hasOverlay = Boolean(datasetInfo.Overlay);

	const selectedPlacemarkInfoPanel = selectedPlacemarkTitle && placemarkItems[selectedPlacemarkTitle]
		? (
			<PlacemarkInfoPanel
				title={selectedPlacemarkTitle}
				closeSelectedPlacemarkInfoPanel={closeSelectedPlacemarkInfoPanel}
				activities={placemarkItems[selectedPlacemarkTitle].activities.filter(
					activity => activity.activityId in filteredActivitiesMap
						&& (!projectMapGroupBy || (`_${projectMapGroupBy}` in activity.properties))
				)}
				datasetInfo={datasetInfo}
			/>
		)
		: null;

	const mapLegend = activityCodeType
		? (
			<MapLegend
				title={activityCodeType.name}
				values={mapLegendValues}
				visibleColorsHashmap={visibleColorsHashmap}
			/>
		)
		: null;

	return (
		<div
			className={classnames('mapView', { 'mapView--picking': mapPinPickMode })}
			ref={mapViewRootRef}
			onScroll={stop}>
			<div id="googleMap" ref={mapContainerRef} />
			{mapPinPickMode
				? (
					<div className="mapPickBanner">
						Click the map to set the distance origin
					</div>
				)
				: null}
			<div className="mapDateSliderContainer">
				<MapRangeSlider
					minDate={datasetInfo.StartDate}
					maxDate={datasetInfo.EndDate}
				/>
			</div>
			<div className="mapLeftChrome">
				{mapLegend}
				{Object.keys(placemarkItems).length
					? <MapMatchReport report={pinMatchReport} />
					: null}
			</div>
			{hasOverlay
				? (
					<div
						className="mapOverlayControl controlButton"
						onClick={toggleOverlay}>
						<div className="controlButton-text">
							{overlayVisible ? 'Hide Background' : 'Show Background'}
						</div>
					</div>
				)
				: null}
			{selectedPlacemarkInfoPanel}
		</div>
	);
};

MapView.propTypes = {
	alert: PropTypes.func,
	filteredActivities: PropTypes.array,
	filteredActivitiesMap: PropTypes.object,
	projectMapGroupBy: PropTypes.string,
	datasetInfo: PropTypes.object,
	proximityDistance: PropTypes.number,
	mapPinMyLocation: PropTypes.object,
	mapPinPickMode: PropTypes.bool,
	setMapPinMyLocation: PropTypes.func,
	setMapPinPickMode: PropTypes.func
};

export default MapView;
